
import React, { useState } from 'react';
import { useGetProductsWithDetails } from '@/hooks/use-products-with-details';
import { useAddSupplierProduct, useRemoveSupplierProduct } from '@/hooks/use-supplier-products';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { X, Loader2, PlusCircle } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from "sonner";
import { SupplierProduct } from '@/types/database.types';

interface SupplierProductsProps {
  supplierId: string;
  supplierProducts: SupplierProduct[];
}

const SupplierProducts: React.FC<SupplierProductsProps> = ({ 
  supplierId, 
  supplierProducts 
}) => {
  const [selectedProduct, setSelectedProduct] = useState<string>('');
  
  const { data: productsWithDetails, isLoading: isProductsLoading } = useGetProductsWithDetails();
  const addSupplierProduct = useAddSupplierProduct();
  const removeSupplierProduct = useRemoveSupplierProduct();
  
  const handleAddProduct = async () => {
    if (!selectedProduct) return;
    
    try {
      const selectedProductData = productsWithDetails?.find(p => p.name === selectedProduct);
      
      if (!selectedProductData) {
        toast.error('Selected product not found');
        return;
      }
      
      await addSupplierProduct.mutateAsync({
        supplier_id: supplierId,
        product_name: selectedProduct,
      });
      
      toast.success(`${selectedProduct} has been added to this supplier`);
      setSelectedProduct('');
    } catch (error) {
      console.error('Error adding product:', error);
      toast.error('Failed to add product. It may already exist for this supplier.');
    }
  };
  
  const handleRemoveProduct = async (productId: string, productName: string) => {
    try {
      await removeSupplierProduct.mutateAsync({
        supplierId,
        productId
      });
      
      toast.success(`Removed ${productName} from supplier products.`);
    } catch (error) {
      console.error('Error removing product:', error);
      toast.error('Failed to remove product.');
    }
  };
  
  // Get unique list of product names from the product details
  const availableProducts = productsWithDetails?.map(product => product.name) || [];
  
  // Filter out products already assigned to the supplier
  const filteredProducts = availableProducts.filter(
    productName => !supplierProducts.some(sp => sp.product_name === productName)
  );
  
  if (isProductsLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {supplierProducts && supplierProducts.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product Name</TableHead>
              <TableHead>Added On</TableHead>
              <TableHead className="w-24">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {supplierProducts.map((product) => (
              <TableRow key={product.id}>
                <TableCell>
                  <Badge variant="outline" className="font-normal">
                    {product.product_name}
                  </Badge>
                </TableCell>
                <TableCell>
                  {new Date(product.created_at).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <Button 
                    variant="outline" 
                    size="icon" 
                    onClick={() => handleRemoveProduct(product.id, product.product_name)}
                    className="h-8 w-8"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <div className="py-6 text-center text-muted-foreground">
          <p>No products have been added for this supplier.</p>
        </div>
      )}

      <div className="flex flex-wrap gap-3 items-end">
        <div className="w-full md:w-auto flex-1">
          <Select
            value={selectedProduct}
            onValueChange={setSelectedProduct}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select product to add" />
            </SelectTrigger>
            <SelectContent>
              {filteredProducts.length > 0 ? (
                filteredProducts.map((product) => (
                  <SelectItem key={product} value={product}>
                    {product}
                  </SelectItem>
                ))
              ) : (
                <SelectItem value="no-products" disabled>
                  No products available
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>
        
        <Button 
          onClick={handleAddProduct} 
          disabled={!selectedProduct || addSupplierProduct.isPending}
          size="sm"
        >
          {addSupplierProduct.isPending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <PlusCircle className="h-4 w-4 mr-2" />
          )}
          Add Product
        </Button>
      </div>
    </div>
  );
};

export default SupplierProducts;
